export type ValidationError =
  | "invalid-date"
  | "required"
  | "before-min"
  | "after-max";

/**
 * Parses a date string in YYYY-MM-DD format into component parts
 * @param dateStr - Date string in format "YYYY-MM-DD"
 * @returns Object with year, month, day numbers, or null if invalid format
 */
export const parseDateStr = (dateStr: string) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    dateStr.length !== 10
  ) {
    return null;
  }

  return { year, month, day };
};

/**
 * Creates a Date object from year, month, day components
 * @param year - Full year (e.g., 2024)
 * @param month - Month number (1-12, not 0-based)
 * @param day - Day of month (1-31)
 */
export const createDate = (year: number, month: number, day: number) => {
  return new Date(year, month - 1, day);
};

/**
 * Validates that a Date object is valid and represents the intended date
 */
export const isValidDate = (
  date: Date | null,
  expectedYear: number,
  expectedMonth: number,
  expectedDay: number,
) => {
  if (!date) return false;
  if (isNaN(date.getTime())) return false;

  // If expected values are provided, verify the date actually represents those values
  if (
    expectedYear !== undefined &&
    expectedMonth !== undefined &&
    expectedDay !== undefined
  ) {
    return (
      date.getFullYear() === expectedYear &&
      date.getMonth() === expectedMonth - 1 && // JavaScript months are 0-based
      date.getDate() === expectedDay
    );
  }

  return true;
};

/**
 * Validates that a date falls within the specified min/max range
 */
export const validateDateRange = (
  date: Date,
  minDate?: string,
  maxDate?: string,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (minDate) {
    const minComponents = parseDateStr(minDate);
    if (minComponents) {
      const minDateObj = createDate(
        minComponents.year,
        minComponents.month,
        minComponents.day,
      );
      if (date < minDateObj) {
        errors.push("before-min");
      }
    }
  }

  if (maxDate) {
    const maxComponents = parseDateStr(maxDate);
    if (maxComponents) {
      const maxDateObj = createDate(
        maxComponents.year,
        maxComponents.month,
        maxComponents.day,
      );
      if (date > maxDateObj) {
        errors.push("after-max");
      }
    }
  }

  return errors;
};

/**
 * Validates date input fields and returns any validation errors
 * Handles validation of individual date components
 */
export const validateDate = (
  monthValue: string,
  dayValue: string,
  yearValue: string,
  required?: boolean,
  minDate?: string,
  maxDate?: string,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (required && (!monthValue || !dayValue || !yearValue)) {
    errors.push("required");
  }

  if (monthValue && dayValue && yearValue) {
    const monthNum = parseInt(monthValue, 10);
    const dayNum = parseInt(dayValue, 10);
    const yearNum = parseInt(yearValue, 10);

    const date = createDate(yearNum, monthNum, dayNum);

    if (!isValidDate(date, yearNum, monthNum, dayNum)) {
      errors.push("invalid-date");
    } else {
      errors.push(...validateDateRange(date, minDate, maxDate));
    }
  }

  return errors;
};

/**
 * Builds an ISO date string (YYYY-MM-DD) from individual components
 * Returns null if components don't form a valid date
 */
export const buildDateString = (
  month: string,
  day: string,
  year: string,
): string | null => {
  if (!month || !day || !year) return null;

  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  const date = createDate(yearNum, monthNum, dayNum);
  if (isValidDate(date, yearNum, monthNum, dayNum)) {
    return `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
  }

  return null;
};
